import React from 'react';
import { Button, InputText, NAICS, Select, SICSearch, Toast } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { ChevronRight } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import FormsSpinner from 'components/common/general/FormSpinner';
import { useHistory } from 'react-router-dom';
import PoweredByIM from 'components/common/general/PoweredByIM';
import { createStyles } from './styles';

const BrowseInfoForm = observer((): JSX.Element => {
  const classes = createStyles();
  const {
    browseInfoStore: {
      loading,
      form,
      effectiveDates,
      ineligibleState,
      canBrowseNext,
      additionalComponentTypes,
      submitBrowseInfoForm,
      overrideSICSearchValue,
      overrideNAICSValue,
      isGroupSizeValid,
    },
    geoStore: { countiesAsSelectOptions, wrongZipCodeKeyedIn, setWrongZipCodeStatus },
    domainStore: { domainHeader },
  } = useRootStore();
  const history = useHistory();

  const convertedEffectiveDates = effectiveDates.map((dateObj: OptionType) => toJS(dateObj)).slice();
  const counties = countiesAsSelectOptions.map((dateObj) => toJS(dateObj)).slice();
  const convertedComponentTypes = additionalComponentTypes.map((dateObj) => toJS(dateObj)).slice();
  const convertedDomain = toJS(domainHeader);
  const stateFieldValue = form.fields.state.value;
  const newProposalAdditionalInputs = convertedDomain.properties?.newProposalAdditionalInputs;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const cart = await submitBrowseInfoForm();
    if (cart?.id) {
      history.push(`/rate-segments?cartId=${cart.id}&groupId=${cart.groupId}&cartType=rate-segment`);
    }
  }

  function getAdditionalInputLabel(inputType: string): string {
    return inputType + (newProposalAdditionalInputs?.optional.includes(inputType) ? ' (optional)' : '');
  }

  function renderAdditionalInputs(): JSX.Element[] {
    // Each component in the store form has its own optional status
    const components: JSX.Element[] = [];

    convertedComponentTypes.forEach((componentType) => {
      if (componentType === 'NAICS') {
        components.push(
          // Note: NAICS component does not support optional status properly.
          // Although incorrect values will not be sent to the backend, they will still be displayed in the component.
          <NAICS
            label={getAdditionalInputLabel(componentType)}
            onCodeUpdate={overrideNAICSValue}
            className={classes.inputSearchComponent}
            width="100%"
            key="naics"
          />,
        );
      }
      if (componentType === 'SIC') {
        components.push(
          <SICSearch
            label={getAdditionalInputLabel(componentType)}
            className={classes.inputSearchComponent}
            width="100%"
            onCodeUpdate={overrideSICSearchValue}
            key="sic"
          />,
        );
      }
      if (componentType === 'GROUP_SIZE') {
        components.push(
          <div className={classes.inputContainer} key="groupSize">
            <InputText
              value={form.fields.groupSize.value}
              onChange={form.onFieldChange}
              name="groupSize"
              label="Estimated Number of Enrolled Employees"
              maxLength={2}
              className={classes.formSelect}
              validationMessage={
                form.fields.groupSize.value.length > 0 && !isGroupSizeValid ? 'Invalid group size, must be 2-50' : ''
              }
              validationPassed={form.fields.groupSize.value.length === 0 || isGroupSizeValid}
            />
          </div>,
        );
      }
    });
    return components;
  }

  return (
    <div className={classes.form}>
      {convertedDomain.properties?.eStoreLogo && (
        <div className={classes.imageWrapper}>
          <img src={convertedDomain.properties.eStoreLogo} alt={convertedDomain.name || 'Sponsor Logo'} />
        </div>
      )}
      <h3 className={classes.formTitle}>Let&apos;s get started</h3>
      <p className={classes.formSubtitle}>When would you like coverage to start and where are you located</p>
      <form className={classes.formContent} onSubmit={onSubmit} id="plan_details_form">
        <Select
          label="Effective Date"
          name="effectiveDate"
          options={convertedEffectiveDates}
          value={form.fields.effectiveDate.value}
          onChange={form.onFieldChange}
          className={classes.formSelect}
        />
        <div className={classes.inputContainer}>
          <InputText
            label="Zip Code"
            name="zipCode"
            value={form.fields.zipCode.value}
            maxLength={5}
            onChange={form.onFieldChange}
            className={classes.formZipCodeInput}
          />
          <Select
            label="County"
            name="county"
            value={form.fields.county.value}
            options={counties}
            onChange={form.onFieldChange}
            className={classes.formCountyInput}
          />
          <InputText label="State" name="state" disabled value={stateFieldValue} className={classes.formStateInput} />
        </div>
        {ineligibleState && <p className={classes.ineligibleStateMessage}>Ineligible state to browse</p>}
        {renderAdditionalInputs()}
      </form>
      <footer>
        <Button
          rightIconProp={<ChevronRight />}
          color="primary"
          htmlType="submit"
          size="large"
          htmlForm="plan_details_form"
          className={classes.submitButton}
          disabled={!canBrowseNext}
        >
          Continue
        </Button>
        <PoweredByIM />
      </footer>
      <FormsSpinner display={loading} />
      <Toast
        show={wrongZipCodeKeyedIn}
        onClose={(): void => {
          setWrongZipCodeStatus(false);
        }}
        colorType={ToastColorTypes.WARNING}
      >
        The keyed in zip code is not valid!
      </Toast>
    </div>
  );
});

export default BrowseInfoForm;
